import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import { useContext } from 'react';

import PartnerLayout from 'blocks/Layouts/PartnerLayout';
import PageService from 'services/PageService/PageService';

import styles from 'styles/pages/partner/index.module.css';
import Image from 'next/image';

import { partnersFooterPNG, partnersHeaderTransparentWEBP } from 'assets';

import { Body, Heading } from 'components/text';
import Container from 'components/container';
import BaseButton from 'components/Button/BaseButton';
import HotelsCarousel from 'components/HotelsCarousel/HotelsCarousel';

import { DialogueContext } from 'customHooks/useDialogue/useDialogue';

import { JOIN_US_DIALOG_STEP_2 } from 'data/constants';

import { getPartners } from 'api/misc';
import type { Partner } from 'api/types';
import type { IGlobalLayout } from 'types/interfaces';
import type { ParsedUrlQuery } from 'node:querystring';
import { JoinUsContext } from 'customHooks/useJoinUs/useJoinUs';

import cx from 'classnames';

type PartnerPageParams = {
  partner: string;
} & ParsedUrlQuery;

type PartnerPageProps = {
  partner: Partner;
  globalLayoutProps: IGlobalLayout;
};

const PartnerPage = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { openDialogue } = useContext(DialogueContext);
  const { joinUsState: state, setState } = useContext(JoinUsContext);
  const { partner, globalLayoutProps } = props;

  const membershipTier = partner.tier ?? 'Travel';

  //list of partners that should have a transparent logo
  // this is a quick fix for making all mastercard partner pages have a transparent logo

  const transparentLogoPartners = [
    'mastercard',
    'horizons',
    'tottenhamhotspurs',
    'monument',
  ];
  const shouldLogoBeTransparent = transparentLogoPartners.includes(
    partner.slug
  );

  const masterCardFirstLine = `Little Emperors is a members-only hotel club, guaranteeing the lowest rates and the most benefits at the best luxury hotels around the world. As a ${partner.name} cardholder, we are delighted to extend a complimentary ${membershipTier} membership to you.`;

  const about = {
    title: 'About Little Emperors',
    contents: [
      `${partner.landing_page_introduction_message || ''}`,
      `With over 5,000 hotel partners, a membership with Little Emperors is the essential travel companion. So whether it’s the Royal Monceau in Paris, the new Six Senses in Rome, the Baccarat in New York or the Four Seasons in Dubai, you can sleep easy knowing you have the best rates and benefits with us.`,
    ],
  };

  const handleSignUpClick = () => {
    setState({
      ...state,
      membershipId: membershipTier.toLocaleLowerCase(),
      promoCode: {
        required: partner.require_promo_code,
        partnerCode: '',
        coupon: partner.coupon,
      },
      requireCard: partner.require_card,
      formType: 'LEPartner',
    });

    openDialogue(JOIN_US_DIALOG_STEP_2);
  };

  const partnerLogoClass = cx(styles.partnerLogo, styles.transparent);

  return (
    <PartnerLayout {...globalLayoutProps}>
      <Container className={styles.heroHeaderContainer}>
        <Container className={styles.headerImageContainer}>
          <Image
            priority
            src={partnersHeaderTransparentWEBP}
            layout="fill"
            objectPosition="center"
            objectFit="cover"
            alt="Little Emperors Partners"
          />
        </Container>

        {partner.logo && (
          <Container className={partnerLogoClass}>
            <Image
              priority
              src={partner.logo}
              layout="fill"
              objectPosition="center"
              objectFit="contain"
              alt="Little Emperors Partners"
            />
          </Container>
        )}

        <Container className={styles.heroHeaderContent}>
          <Body fontSize={16} weight="demiBold" color="white">
            Enjoy a complimentary
          </Body>
          <Heading
            fontSize={19}
            weight="demiBold"
            mt={5}
            as="h5"
            color="white"
            letterSpacing="0.285em"
          >
            {membershipTier.toUpperCase()} MEMBERSHIP
          </Heading>
        </Container>
      </Container>

      <Container className={styles.content}>
        <Container className={styles.about}>
          {about.contents.map((content, i) => {
            return (
              <Heading
                mt={i === 1 ? 20 : 0}
                key={i.toString()}
                as="h5"
                color="grey-dark"
              >
                {content}
              </Heading>
            );
          })}
        </Container>

        <Container className={styles.benefits}>
          {benefits.contents.map(benefit => {
            return (
              <Container key={benefit.title}>
                <Heading as="h4" color="gold-dark">
                  {benefit.title}
                </Heading>
                <Body color="grey-dark">{benefit.text}</Body>
              </Container>
            );
          })}
        </Container>
      </Container>

      <Container className={styles.heroFooterContainer}>
        <Container className={styles.headerImageContainer}>
          <Image
            src={partnersFooterPNG}
            layout="fill"
            objectPosition="center"
            objectFit="cover"
            alt="Little Emperors Partners"
          />
        </Container>

        <Container className={styles.topGradient} />

        <Container className={styles.heroFooterContent}>
          <Container>
            <Heading as="h4">Claim your membership</Heading>

            <Body color="grey-dark">
              Join the club now to unlock a world of incredible hotels at the
              best rates and with the most benefits.
            </Body>
          </Container>

          <BaseButton
            size="full"
            onClick={handleSignUpClick}
            className={styles.button}
          >
            Sign up
          </BaseButton>
        </Container>
      </Container>
      <div className={styles.carousel}>
        <HotelsCarousel isPartner />
      </div>
    </PartnerLayout>
  );
};

export default PartnerPage;

export const getStaticProps: GetStaticProps<
  PartnerPageProps,
  PartnerPageParams
> = async context => {
  const partnerSlug = context.params?.partner.toLowerCase();

  const partners = await getPartners();
  const partner = partners.find(p => p.slug === partnerSlug);

  if (!partner) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  const { globalLayoutProps } = await PageService.getGenericPageProps({
    navPath: `/${partner}`,
  });

  const metaTitle = `${partner.name} - Little Emperors`;

  globalLayoutProps.metaData = {
    ...globalLayoutProps.metaData,
    title: metaTitle,
    description: metaTitle,
  };

  const props = {
    partner,
    globalLayoutProps,
  };

  return {
    props,
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const partners = await getPartners();

  const paths = partners
    .filter(
      partner =>
        partner.slug &&
        partner.slug !== 'pressgift' && // Has its own static page at /pressgift
        partner.slug !== 'en' // Conflicts with i18n locale path /en during build
    )
    .map(partner => {
      return {
        params: {
          partner: partner.slug,
        },
      };
    });

  return {
    paths,
    fallback: 'blocking',
  };
};

const benefits = {
  title:
    'There are so many reasons to be part of the club - here are just a few:',
  contents: [
    {
      title: 'The lowest prices, guaranteed',
      text: 'The best prices at more than 5,000 handpicked, luxury hotels across the globe. Enjoy your stay, safe in the knowledge that your booking matches the cancellation, payment and loyalty-point collection as when booking with hotels directly.',
    },
    {
      title: 'Real benefits',
      text: 'We only present our members with actual benefits. Breakfasts, hotel credits, confirmed upgrades and add-on experiences are just some of the complimentary extras our members are eligible for.',
    },
    {
      title: 'A personalised experience',
      text: 'Our portfolio of hotels on our app and website are personalised with relevant offers and recommendations, just for you. Our members enjoy our state-of-the-art technology (and booking in just a few clicks).',
    },
    {
      title: 'Real people',
      text: 'Our team of London-based travel advisors is ready to guide you via phone, email and WhatsApp, sharing insider knowledge to ensure an unforgettable trip.',
    },
  ],
};
